import { z } from 'zod';
import { idSchema, isoDateSchema, isoDateTimeSchema, paginationQuerySchema } from './primitives';
import { ingestionSourceSchema } from './scanning';
/**
 * Reçus archivés (cahier des charges §3, §7, §10, §11).
 *
 * Le numéro de commande est la clé d'usage du système. Un reçu peut porter un
 * sous-numéro ; le cahier des charges précise que ce sous-numéro fait parfois
 * office de numéro principal lorsque le numéro long est absent du document.
 * `searchKey` matérialise cette règle : c'est le numéro que l'utilisateur
 * saisira pour retrouver le document.
 */
export const RECEIPT_STATUSES = {
    IMPORTED: 'IMPORTED',
    PROCESSING: 'PROCESSING',
    OCR_PENDING: 'OCR_PENDING',
    NEEDS_REVIEW: 'NEEDS_REVIEW',
    DUPLICATE_SUSPECTED: 'DUPLICATE_SUSPECTED',
    READY: 'READY',
    ARCHIVED: 'ARCHIVED',
    REJECTED: 'REJECTED',
};
export const receiptStatusSchema = z.enum([
    RECEIPT_STATUSES.IMPORTED,
    RECEIPT_STATUSES.PROCESSING,
    RECEIPT_STATUSES.OCR_PENDING,
    RECEIPT_STATUSES.NEEDS_REVIEW,
    RECEIPT_STATUSES.DUPLICATE_SUSPECTED,
    RECEIPT_STATUSES.READY,
    RECEIPT_STATUSES.ARCHIVED,
    RECEIPT_STATUSES.REJECTED,
]);
export const RECEIPT_STATUS_LABELS = {
    IMPORTED: 'Importé',
    PROCESSING: 'Traitement image',
    OCR_PENDING: 'OCR en attente',
    NEEDS_REVIEW: 'Validation requise',
    DUPLICATE_SUSPECTED: 'Doublon suspecté',
    READY: 'Prêt à archiver',
    ARCHIVED: 'Archivé',
    REJECTED: 'Rejeté',
};
/**
 * Numéro de commande : chiffres et lettres, tirets et espaces tolérés à la
 * saisie puis normalisés (majuscules, séparateurs retirés) avant indexation.
 */
export const orderNumberSchema = z
    .string()
    .trim()
    .min(3, 'Numéro de commande trop court')
    .max(40, 'Numéro de commande trop long')
    .regex(/^[A-Za-z0-9][A-Za-z0-9 \-/]*$/, 'Caractères non autorisés dans le numéro de commande');
export const subNumberSchema = z
    .string()
    .trim()
    .min(1)
    .max(24)
    .regex(/^[A-Za-z0-9][A-Za-z0-9\-/]*$/, 'Sous-numéro invalide');
/** Normalise un numéro pour la comparaison et l'indexation. */
export function normalizeOrderNumber(value) {
    return value.toUpperCase().replace(/[\s\-/]+/g, '');
}
export const receiptPageSchema = z.object({
    id: idSchema,
    pageNumber: z.number().int().positive(),
    widthPx: z.number().int().positive(),
    heightPx: z.number().int().positive(),
    deskewAngle: z.number(),
    blankScore: z.number().min(0).max(1),
    hasSignature: z.boolean(),
});
export const receiptItemSchema = z.object({
    id: idSchema,
    lineNumber: z.number().int().positive(),
    description: z.string(),
    quantity: z.number().nullable(),
    unitPrice: z.number().nullable(),
    totalPrice: z.number().nullable(),
});
/** Projection de liste : volontairement légère, la recherche doit rester sous 2 s. */
export const receiptSummarySchema = z.object({
    id: idSchema,
    orderNumber: z.string().nullable(),
    subNumber: z.string().nullable(),
    searchKey: z.string(),
    status: receiptStatusSchema,
    branchId: idSchema,
    branchCode: z.string(),
    branchName: z.string(),
    lotId: idSchema,
    lotReference: z.string(),
    deliveryDate: isoDateSchema.nullable(),
    customerName: z.string().nullable(),
    driverName: z.string().nullable(),
    driverPlate: z.string().nullable(),
    pageCount: z.number().int().nonnegative(),
    hasSignature: z.boolean(),
    ocrConfidence: z.number().min(0).max(1).nullable(),
    archivedAt: isoDateTimeSchema.nullable(),
    createdAt: isoDateTimeSchema,
});
export const receiptDetailSchema = receiptSummarySchema.extend({
    totalAmount: z.number().nullable(),
    currency: z.string().nullable(),
    issueDate: isoDateSchema.nullable(),
    documentType: z.string().nullable(),
    source: ingestionSourceSchema,
    fileHash: z.string(),
    fileSizeBytes: z.number().int().nonnegative(),
    archivedByName: z.string().nullable(),
    operatorName: z.string(),
    pages: z.array(receiptPageSchema),
    items: z.array(receiptItemSchema),
    updatedAt: isoDateTimeSchema,
});
/** Correction manuelle des métadonnées (fallback OCR, §6). */
export const correctReceiptSchema = z
    .object({
    orderNumber: orderNumberSchema.nullable().optional(),
    subNumber: subNumberSchema.nullable().optional(),
    deliveryDate: isoDateSchema.nullable().optional(),
    issueDate: isoDateSchema.nullable().optional(),
    customerName: z.string().trim().max(160).nullable().optional(),
    driverName: z.string().trim().max(120).nullable().optional(),
    driverPlate: z.string().trim().max(24).nullable().optional(),
    totalAmount: z.number().nullable().optional(),
    currency: z.string().trim().length(3).nullable().optional(),
    hasSignature: z.boolean().optional(),
    reason: z.string().trim().max(280).optional(),
})
    .refine((data) => Boolean(data.orderNumber) || Boolean(data.subNumber) || Object.keys(data).length > 0, 'Aucune modification fournie');
export const SIGNATURE_FILTERS = {
    ANY: 'ANY',
    PRESENT: 'PRESENT',
    ABSENT: 'ABSENT',
};
export const signatureFilterSchema = z.enum([
    SIGNATURE_FILTERS.ANY,
    SIGNATURE_FILTERS.PRESENT,
    SIGNATURE_FILTERS.ABSENT,
]);
/**
 * Recherche documentaire (§10).
 * `orderNumber` seul correspond à la recherche principale ; les autres champs
 * composent la recherche avancée.
 */
export const searchReceiptsQuerySchema = paginationQuerySchema.extend({
    orderNumber: z.string().trim().max(40).optional(),
    subNumber: z.string().trim().max(24).optional(),
    branchId: idSchema.optional(),
    lotId: idSchema.optional(),
    operatorUserId: idSchema.optional(),
    status: receiptStatusSchema.optional(),
    dateFrom: isoDateSchema.optional(),
    dateTo: isoDateSchema.optional(),
    customerName: z.string().trim().max(160).optional(),
    driverName: z.string().trim().max(120).optional(),
    signature: signatureFilterSchema.default('ANY'),
    sortBy: z.enum(['relevance', 'deliveryDate', 'archivedAt', 'orderNumber']).default('relevance'),
    sortDirection: z.enum(['asc', 'desc']).default('desc'),
});
export const searchResponseSchema = z.object({
    items: z.array(receiptSummarySchema),
    page: z.number().int(),
    pageSize: z.number().int(),
    total: z.number().int(),
    totalPages: z.number().int(),
    /** Temps d'exécution serveur, exposé pour tenir l'exigence des 2 secondes. */
    elapsedMs: z.number().nonnegative(),
});
/* ------------------------------------------------------------------------ */
/* Doublons (§7)                                                             */
/* ------------------------------------------------------------------------ */
export const DUPLICATE_MATCH_TYPES = {
    FILE_HASH: 'FILE_HASH',
    PERCEPTUAL: 'PERCEPTUAL',
    BUSINESS_KEY: 'BUSINESS_KEY',
};
export const duplicateMatchTypeSchema = z.enum([
    DUPLICATE_MATCH_TYPES.FILE_HASH,
    DUPLICATE_MATCH_TYPES.PERCEPTUAL,
    DUPLICATE_MATCH_TYPES.BUSINESS_KEY,
]);
export const DUPLICATE_MATCH_LABELS = {
    FILE_HASH: 'Fichier identique',
    PERCEPTUAL: 'Image quasi identique',
    BUSINESS_KEY: 'Même commande, bureau et date',
};
export const DUPLICATE_RESOLUTIONS = {
    PENDING: 'PENDING',
    IGNORED: 'IGNORED',
    REPLACED: 'REPLACED',
    DISTINCT: 'DISTINCT',
};
export const duplicateResolutionSchema = z.enum([
    DUPLICATE_RESOLUTIONS.PENDING,
    DUPLICATE_RESOLUTIONS.IGNORED,
    DUPLICATE_RESOLUTIONS.REPLACED,
    DUPLICATE_RESOLUTIONS.DISTINCT,
]);
export const duplicateCandidateSchema = z.object({
    id: idSchema,
    matchType: duplicateMatchTypeSchema,
    score: z.number().min(0).max(1),
    resolution: duplicateResolutionSchema,
    detectedAt: isoDateTimeSchema,
    resolvedAt: isoDateTimeSchema.nullable(),
    resolvedByName: z.string().nullable(),
    note: z.string().nullable(),
    incoming: receiptSummarySchema,
    existing: receiptSummarySchema,
});
export const resolveDuplicateSchema = z.object({
    resolution: z.enum([
        DUPLICATE_RESOLUTIONS.IGNORED,
        DUPLICATE_RESOLUTIONS.REPLACED,
        DUPLICATE_RESOLUTIONS.DISTINCT,
    ]),
    note: z.string().trim().max(280).optional(),
});
export const listDuplicatesQuerySchema = paginationQuerySchema.extend({
    branchId: idSchema.optional(),
    lotId: idSchema.optional(),
    resolution: duplicateResolutionSchema.optional(),
});
//# sourceMappingURL=receipts.js.map